import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { brand } from "@/lib/brand";
import { getProductById } from "@/lib/product-data";

type SeoConfig = {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
};

const defaultDescription =
  "MOKA connects school payments, identity, transport, communication, and learning in one digital operating system for education.";

const pages: Record<string, Omit<SeoConfig, "path">> = {
  "/": {
    title: "MOKA | The Digital Operating System for Education",
    description: defaultDescription,
  },
  "/parents": {
    title: "School Payments and Family Services for Parents | MOKA",
    description:
      "Pay school fees, track transport, manage meals, and stay connected to your child's school from one family platform.",
  },
  "/schools": {
    title: "School Fee Collection and Administration Platform | MOKA",
    description:
      "Simplify school fee collection, reconciliation, parent communication, and campus operations with MOKA.",
  },
  "/merchants": {
    title: "Cashless Campus Payments for School Merchants | MOKA",
    description:
      "Accept student wallet payments, manage campus sales, and receive clear daily settlements with MOKA.",
  },
  "/about": {
    title: "About MOKA | Education Technology Built in South Africa",
    description:
      "Learn how MOKA is building connected digital services for schools, families, students, and education partners across Africa.",
  },
  "/demo": {
    title: "Explore the MOKA Platform | Interactive Demos",
    description:
      "Explore interactive demonstrations of MOKA fleet tracking, payments, revenue flows, architecture, and student cards.",
  },
  "/ecosystem": {
    title: "The MOKA Education Technology Ecosystem",
    description:
      "Explore MOKA's connected products for identity, learning, transport, communication, campus services, intelligence, and payments.",
  },
  "/insights": {
    title: "Education Finance and Payment Insights | MOKA",
    description:
      "See real-time education finance analytics, collection trends, payment velocity, forecasting, and operational metrics.",
  },
  "/auth": {
    title: "Sign In to MOKA",
    description: "Access your MOKA education services portal.",
    noindex: true,
  },
  "/app": {
    title: "MOKA Portal",
    description: "Your connected MOKA education services portal.",
    noindex: true,
  },
};

function setMeta(name: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setProperty(property: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(url: string): void {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = url;
}

function setStructuredData(config: SeoConfig, url: string): void {
  const id = "moka-structured-data";
  let element = document.getElementById(id);
  if (!element) {
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    element = script;
    document.head.appendChild(script);
  }
  element.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brand.name,
    legalName: brand.company,
    url: `https://${brand.domain}`,
    description: config.description,
    address: {
      "@type": "PostalAddress",
      addressLocality: brand.city,
      addressCountry: brand.country,
    },
    sameAs: [url],
  });
}

export function SiteSeo(): null {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const normalizedPathname = pathname.replace(/\/$/, "") || "/";
    const productId = normalizedPathname.startsWith("/ecosystem/")
      ? normalizedPathname.split("/")[2]
      : undefined;
    const product = productId ? getProductById(productId) : undefined;
    const page = pages[normalizedPathname] ?? (product
      ? {
          title: `${product.name} | MOKA Education Platform`,
          description: product.description,
        }
      : {
          title: `${brand.name} | Education Technology Platform`,
          description: defaultDescription,
          noindex: true,
        });
    const config: SeoConfig = { ...page, path: normalizedPathname };
    const url = `https://${brand.domain}${normalizedPathname === "/" ? "/" : normalizedPathname}`;

    document.title = config.title;
    setMeta("description", config.description);
    setMeta("robots", config.noindex ? "noindex, nofollow" : "index, follow");
    setProperty("og:title", config.title);
    setProperty("og:description", config.description);
    setProperty("og:type", "website");
    setProperty("og:url", url);
    setProperty("og:site_name", brand.name);
    setProperty("og:locale", "en_UG");
    setMeta("twitter:title", config.title);
    setMeta("twitter:description", config.description);
    setCanonical(url);
    setStructuredData(config, url);
  }, [pathname]);

  return null;
}
